import { TokenType, type Token, type SourceLocation } from "../types/tokens.js";

const KEYWORDS: Record<string, TokenType> = {
  system: TokenType.System,
  service: TokenType.Service,
  domain: TokenType.Domain,
  usecase: TokenType.Usecase,
  entity: TokenType.Entity,
  resource: TokenType.Resource,
  capability: TokenType.Capability,
  user: TokenType.User,
  client: TokenType.Client,
  deploy: TokenType.Deploy,
  war: TokenType.War,
  jar: TokenType.Jar,
  oci: TokenType.Oci,
  lambda: TokenType.Lambda,
  function: TokenType.Function,
  assets: TokenType.Assets,
  job: TokenType.Job,
  artifact: TokenType.Artifact,
  runtime: TokenType.Runtime,
  realizes: TokenType.Realizes,
  handles: TokenType.Handles,
  operations: TokenType.Operations,
  delivers: TokenType.Delivers,
  schedule: TokenType.Schedule,
  image: TokenType.Image,
  type: TokenType.Type,
  label: TokenType.Label,
  role: TokenType.Role,
  description: TokenType.Description,
  team: TokenType.Team,
  link: TokenType.Link,
  organization: TokenType.Organization,
  member: TokenType.Member,
  owns: TokenType.Owns,
  boundary: TokenType.Boundary,
  contains: TokenType.Contains,
  facet: TokenType.Facet,
  facets: TokenType.Facets,
  slack: TokenType.Slack,
  github: TokenType.Github,
  import: TokenType.Import,
  from: TokenType.From,
  database: TokenType.Database,
  queue: TokenType.Queue,
  storage: TokenType.Storage,
  table: TokenType.Table,
  bucket: TokenType.Bucket,
  legend: TokenType.Legend,
  swatch: TokenType.Swatch,
  ref: TokenType.Ref,
};

/**
 * The lexer's keyword names, derived from the same table the lexer matches
 * against. Consumers that maintain their own hand-copied keyword list (e.g.
 * LSP completion) can diff against this to detect drift.
 */
export const KRS_KEYWORD_NAMES = Object.keys(KEYWORDS);

/**
 * The token types the lexer reserves for keywords. Vocabulary positions that
 * accept keyword spellings as ordinary names (tags, kebab-name fragments —
 * see parser/kebab-name.ts) test membership here instead of hand-copying the
 * keyword list.
 */
export const KRS_KEYWORD_TOKEN_TYPES: ReadonlySet<TokenType> = new Set(Object.values(KEYWORDS));

/**
 * Whether `value` has the shape the lexer reads as a single identifier word
 * (`[\p{L}_][\p{L}\p{M}\p{N}_]*`, plus a ZWNJ / ZWJ where `wordPartAt`
 * allows one), from the same character tests `readToken` uses.
 * Keyword spellings pass this check; they arrive as keyword tokens, so a caller
 * that also needs "not a keyword" tests the token type.
 */
export function isBareWord(value: string): boolean {
  // Walk by code point with `wordPartAt`, as `readToken` reads, so both agree
  // on every string.
  if (!isIdentStart(codePointAt(value, 0))) return false;
  let prev = "";
  for (let i = 0; i < value.length;) {
    const ch = wordPartAt(value, i, prev);
    if (ch === "") return false;
    i += ch.length;
    prev = ch;
  }
  return true;
}

export class Lexer {
  private source: string;
  private pos = 0;
  private line = 1;
  private column = 1;

  constructor(source: string) {
    this.source = source;
  }

  tokenize(): Token[] {
    const tokens: Token[] = [];
    while (this.pos < this.source.length) {
      this.skipWhitespace();
      this.skipComment();
      if (this.pos >= this.source.length) break;

      const token = this.readToken();
      // The cursor now sits just past the token — whitespace is skipped at the
      // top of the loop, not here — so this is the token's exact end.
      if (token) tokens.push(this.stampEnd(token));
    }
    tokens.push(this.makeToken(TokenType.EOF, ""));
    return tokens;
  }

  /**
   * Tokenize including comment tokens (LineComment / BlockComment).
   * Used by the formatter to preserve comments.
   * The parser always uses tokenize() which discards comments.
   */
  tokenizeWithComments(): Token[] {
    const tokens: Token[] = [];
    while (this.pos < this.source.length) {
      this.skipWhitespace();
      if (this.pos >= this.source.length) break;

      const comment = this.readComment();
      if (comment) {
        tokens.push(this.stampEnd(comment));
        continue;
      }

      const token = this.readToken();
      if (token) tokens.push(this.stampEnd(token));
    }
    tokens.push(this.makeToken(TokenType.EOF, ""));
    return tokens;
  }

  private loc(): SourceLocation {
    return { line: this.line, column: this.column, offset: this.pos };
  }

  private makeToken(type: TokenType, value: string): Token {
    return { type, value, loc: this.loc() };
  }

  /** Records where the just-read token ends, from the cursor's position. */
  private stampEnd(token: Token): Token {
    token.end = this.loc();
    return token;
  }

  /**
   * The code point at the cursor, so a character outside the BMP is one
   * character to every test, not two surrogate halves that each fail it (#2848).
   */
  private peek(): string {
    return codePointAt(this.source, this.pos);
  }

  private peekAt(offset: number): string {
    return this.source[this.pos + offset] ?? "";
  }

  /**
   * Consume one code point. `offset` and `column` stay in UTF-16 units, the
   * unit LSP positions and `String#slice` use, so a surrogate pair moves both
   * by two.
   */
  private advance(): string {
    const ch = codePointAt(this.source, this.pos);
    this.pos += ch.length;
    if (ch === "\n") {
      this.line++;
      this.column = 1;
    } else {
      this.column += ch.length;
    }
    return ch;
  }

  private skipWhitespace(): void {
    while (this.pos < this.source.length) {
      const ch = this.peek();
      if (isWhitespace(ch)) {
        this.advance();
      } else {
        break;
      }
    }
  }

  /** Discard a single comment (line or block) if the cursor is at one. */
  private skipComment(): void {
    const ch = this.peek();

    if (ch === "/" && this.peekAt(1) === "/") {
      this.advance();
      this.advance();
      while (this.pos < this.source.length && this.peek() !== "\n") {
        this.advance();
      }
      this.skipWhitespace();
      this.skipComment();
      return;
    }

    if (ch === "/" && this.peekAt(1) === "*") {
      this.advance();
      this.advance();
      while (this.pos < this.source.length) {
        if (this.peek() === "*" && this.peekAt(1) === "/") {
          this.advance();
          this.advance();
          break;
        }
        this.advance();
      }
      this.skipWhitespace();
      this.skipComment();
    }
  }

  /**
   * If the cursor is at a comment, read and return a comment Token.
   * Returns null if the cursor is not at a comment.
   */
  private readComment(): Token | null {
    const ch = this.peek();

    if (ch === "/" && this.peekAt(1) === "/") {
      const loc = this.loc();
      this.advance(); // /
      this.advance(); // /
      let value = "";
      while (this.pos < this.source.length && this.peek() !== "\n") {
        value += this.advance();
      }
      return { type: TokenType.LineComment, value: value.trim(), loc };
    }

    if (ch === "/" && this.peekAt(1) === "*") {
      const loc = this.loc();
      this.advance(); // /
      this.advance(); // *
      let value = "";
      while (this.pos < this.source.length) {
        if (this.peek() === "*" && this.peekAt(1) === "/") {
          this.advance();
          this.advance();
          break;
        }
        value += this.advance();
      }
      return { type: TokenType.BlockComment, value: value.trim(), loc };
    }

    return null;
  }

  private readToken(): Token | null {
    const ch = this.peek();
    const loc = this.loc();

    switch (ch) {
      case "{":
        this.advance();
        return { type: TokenType.LeftBrace, value: "{", loc };
      case "}":
        this.advance();
        return { type: TokenType.RightBrace, value: "}", loc };
      case "[":
        this.advance();
        return { type: TokenType.LeftBracket, value: "[", loc };
      case "]":
        this.advance();
        return { type: TokenType.RightBracket, value: "]", loc };
      case "(":
        this.advance();
        return { type: TokenType.LeftParen, value: "(", loc };
      case ")":
        this.advance();
        return { type: TokenType.RightParen, value: ")", loc };
      case ",":
        this.advance();
        return { type: TokenType.Comma, value: ",", loc };
      case ":":
        this.advance();
        return { type: TokenType.Colon, value: ":", loc };
      case '"':
        if (this.peekAt(1) === '"' && this.peekAt(2) === '"') {
          return this.readTripleQuoteString(loc);
        }
        return this.readString(loc);
      case "-":
        return this.readArrow(loc);
      case "@":
        return this.readAtToken(loc);
      case ".":
        this.advance();
        return { type: TokenType.Dot, value: ".", loc };
      case "#":
        // Used by legend blocks for hex colors (`#2563EB`) and id-selector
        // references (`#NodeId`). Emit the `#` + the immediately following
        // identifier-or-digit chars as a single Identifier token so the
        // parser can disambiguate by inspecting the value. Style sheets
        // have a parallel implementation in style-lexer.ts.
        return this.readHashToken(loc);
      default:
        if (isIdentStart(ch)) {
          return this.readIdentifierOrKeyword(loc);
        }
        if (isDigit(ch)) {
          return this.readNumber(loc);
        }
        if (!isAscii(ch)) return this.readUnknown(loc);
        // Skip any other ASCII character. The parser never learns it was there,
        // so it cannot refuse it. The `.krs` in `examples/` and in linted doc
        // fences relies on this only for `=` and `;` (`label = "x"`,
        // `runtime "n"; realizes X`), and `lexer-discard.test.ts` pins the
        // dropped set, so one that starts landing here is a visible change.
        // Digits used to land here, which turned `until: 2026-12-31` into
        // `until: "-"` (#2707), and so did every non-ASCII character that is
        // not a letter, which turned `service 😀A` into `service A` (#3093).
        this.advance();
        return null;
    }
  }

  /**
   * Read a non-ASCII character that no other branch takes, plus any word
   * characters after it, as one Unknown token (`😀A`), so a diagnostic covers
   * what the author wrote and `A` is not left behind to be read as a name
   * (#3093). The same shape as `readNumber`.
   */
  private readUnknown(loc: SourceLocation): Token {
    return { type: TokenType.Unknown, value: this.readWordTail(this.advance()), loc };
  }

  /**
   * Consume the word characters after `head` (already consumed) and return
   * `head` with them appended. Every word-shaped token ends where this stops.
   */
  private readWordTail(head: string): string {
    let value = head;
    let prev = head;
    for (let ch = wordPartAt(this.source, this.pos, prev); ch !== "";) {
      value += this.advance();
      prev = ch;
      ch = wordPartAt(this.source, this.pos, prev);
    }
    return value;
  }

  private readString(loc: SourceLocation): Token {
    this.advance(); // opening "
    let value = "";
    while (this.pos < this.source.length && this.peek() !== '"') {
      if (this.peek() === "\\") {
        this.advance();
        const escaped = this.advance();
        if (escaped === '"') value += '"';
        else if (escaped === "\\") value += "\\";
        else if (escaped === "n") value += "\n";
        else value += escaped;
      } else {
        value += this.advance();
      }
    }
    if (this.peek() === '"') this.advance(); // closing "
    return { type: TokenType.StringLiteral, value, loc };
  }

  private readTripleQuoteString(loc: SourceLocation): Token {
    // Consume opening """
    this.advance(); // "
    this.advance(); // "
    this.advance(); // "

    // Skip immediate newline after opening """
    if (this.peek() === "\n") {
      this.advance();
    } else if (this.peek() === "\r" && this.peekAt(1) === "\n") {
      this.advance();
      this.advance();
    }

    let raw = "";
    while (this.pos < this.source.length) {
      if (this.peek() === '"' && this.peekAt(1) === '"' && this.peekAt(2) === '"') {
        break;
      }
      raw += this.advance();
    }

    // Consume closing """
    if (this.peek() === '"') this.advance();
    if (this.peek() === '"') this.advance();
    if (this.peek() === '"') this.advance();

    // Dedent: use the indentation of the closing """ line as the common prefix
    const value = dedentTripleQuote(raw);
    return { type: TokenType.TripleQuote, value, loc };
  }

  private readArrow(loc: SourceLocation): Token {
    this.advance(); // first -
    if (this.peek() === "-") {
      this.advance(); // second -
      if (this.peek() === ">") {
        this.advance(); // >
        return { type: TokenType.DashedArrow, value: "-->", loc };
      }
      // Not a valid token, treat as unknown
      return { type: TokenType.Identifier, value: "--", loc };
    }
    if (this.peek() === ">") {
      this.advance(); // >
      return { type: TokenType.Arrow, value: "->", loc };
    }
    return { type: TokenType.Identifier, value: "-", loc };
  }

  private readAtToken(loc: SourceLocation): Token {
    this.advance(); // @
    // Check if followed by "import"
    const ahead = this.peekWord();
    if (ahead === "import") {
      // Consume "import"
      for (let i = 0; i < 6; i++) this.advance();
      return { type: TokenType.AtImport, value: "@import", loc };
    }
    return { type: TokenType.At, value: "@", loc };
  }

  private peekWord(): string {
    let word = "";
    let prev = "";
    for (let ch = wordPartAt(this.source, this.pos, prev); ch !== "";) {
      word += ch;
      prev = ch;
      ch = wordPartAt(this.source, this.pos + word.length, prev);
    }
    return word;
  }

  private readIdentifierOrKeyword(loc: SourceLocation): Token {
    const value = this.readWordTail(this.advance());
    const kwType = KEYWORDS[value];
    if (kwType) {
      return { type: kwType, value, loc };
    }
    return { type: TokenType.Identifier, value, loc };
  }

  /**
   * Read a word that starts with a digit (`2026`, `2026abc`) as one Number
   * token. The run continues through identifier characters so a diagnostic
   * covers the whole word the author wrote, not just its leading digits.
   */
  private readNumber(loc: SourceLocation): Token {
    return { type: TokenType.Number, value: this.readWordTail(this.advance()), loc };
  }

  /**
   * Read `#<hex-or-ident>+` as a single Identifier (e.g. `#2563EB`,
   * `#NodeId`). Used for legend swatch colors and ref id-selector targets.
   */
  private readHashToken(loc: SourceLocation): Token {
    return { type: TokenType.Identifier, value: this.readWordTail(this.advance()), loc };
  }
}

/** Any numeric character, matching the `\p{N}` that `isIdentPart` accepts. */
function isDigit(ch: string): boolean {
  const c = ch.charCodeAt(0);
  if (c < 0x80) return c >= 0x30 && c <= 0x39;
  return /\p{N}/u.test(ch);
}

/**
 * ASCII space, tab, CR and LF, every other character with the Unicode
 * `White_Space` property (U+3000, NBSP, U+2028, ...), and the byte order mark,
 * which nothing strips before lexing (#3093). Close to ECMAScript's
 * WhiteSpace and LineTerminator. Only `\n` starts a new line, so line numbers
 * stay the ones LSP and the editor count.
 */
export function isWhitespace(ch: string): boolean {
  const c = ch.charCodeAt(0);
  // ASCII `White_Space`: tab, LF, VT, FF, CR and space.
  if (c < 0x80) return c === 0x20 || (c >= 0x09 && c <= 0x0d);
  return ch === "\uFEFF" || /\p{White_Space}/u.test(ch);
}

function isAscii(ch: string): boolean {
  return ch.length === 1 && ch.charCodeAt(0) <= 0x7f;
}

// The character tests below answer ASCII by range before reaching a `\p{...}`
// regex. The style lexer imports them, so a name reads the same in a `.krs`
// file and in a `.krs.style` selector (#3140, TPL-2509): the lexer calls them once per character, and most `.krs` outside
// strings is ASCII. `lexer.test.ts` checks every ASCII character against the
// regex form.

export function isIdentStart(ch: string): boolean {
  const c = ch.charCodeAt(0);
  if (c < 0x80) return isAsciiLetter(c) || c === 0x5f;
  return /[\p{L}_]/u.test(ch);
}

/**
 * A combining mark continues a word but cannot start one: it modifies the
 * character before it, so a decomposed `café` is one word (#2848).
 */
export function isIdentPart(ch: string): boolean {
  const c = ch.charCodeAt(0);
  if (c < 0x80) return isAsciiLetter(c) || (c >= 0x30 && c <= 0x39) || c === 0x5f;
  return /[\p{L}\p{M}\p{N}_]/u.test(ch);
}

function isAsciiLetter(c: number): boolean {
  return (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a);
}

/**
 * Scripts in which ZWNJ / ZWJ change how the letters around them are drawn:
 * the cursive-joining scripts and the Indic scripts that form conjuncts after
 * a virama. Outside them a joiner is invisible, so `A\u200C` would look exactly
 * like `A` while being another id (#3093). An approximation of the contexts
 * UAX #31 allows joiners in, which JavaScript cannot test directly.
 */
const JOINER_SCRIPTS =
  /[\p{scx=Arabic}\p{scx=Syriac}\p{scx=Mandaic}\p{scx=Mongolian}\p{scx=Nko}\p{scx=Adlam}\p{scx=Devanagari}\p{scx=Bengali}\p{scx=Gurmukhi}\p{scx=Gujarati}\p{scx=Oriya}\p{scx=Tamil}\p{scx=Telugu}\p{scx=Kannada}\p{scx=Malayalam}\p{scx=Sinhala}\p{scx=Myanmar}\p{scx=Khmer}]/u;

/**
 * The word character at UTF-16 index `i` of `s`, or `""` where the word ends.
 * `prev` is the character before it. Most characters decide alone
 * (`isIdentPart`); ZWNJ / ZWJ (U+200C / U+200D) continue a word only between
 * a letter or mark of a `JOINER_SCRIPTS` script and another letter or mark, so
 * a Persian or Indic name stays whole while a stray joiner after a Latin
 * letter or at the end of a name is refused (#3093).
 */
export function wordPartAt(s: string, i: number, prev: string): string {
  const ch = codePointAt(s, i);
  if (ch === "" || isIdentPart(ch)) return ch;
  if (ch !== "\u200C" && ch !== "\u200D") return "";
  const next = codePointAt(s, i + ch.length);
  const joins =
    /[\p{L}\p{M}]/u.test(prev) && JOINER_SCRIPTS.test(prev) && /[\p{L}\p{M}]/u.test(next);
  return joins ? ch : "";
}

/**
 * The code point starting at UTF-16 index `i`, or `""` past the end. A unit
 * that is not a surrogate is the whole code point, so it is returned without
 * building a new string.
 */
export function codePointAt(s: string, i: number): string {
  const c = s.charCodeAt(i);
  if (c < 0xd800 || c > 0xdfff) return s[i];
  const cp = s.codePointAt(i);
  return cp === undefined ? "" : String.fromCodePoint(cp);
}

/**
 * Dedent triple-quoted string content.
 * Uses the indentation of the last line (closing """ line) as the common prefix to strip.
 * Trailing newline before closing """ is removed.
 */
function dedentTripleQuote(raw: string): string {
  const lines = raw.split("\n");

  // The last line contains only the indentation of the closing """
  const lastLine = lines[lines.length - 1];
  const indent = lastLine.match(/^(\s*)/)?.[1] ?? "";

  // Remove the last line (closing """ indent only) if it's whitespace-only
  if (/^\s*$/.test(lastLine)) {
    lines.pop();
  }

  if (indent.length === 0) return lines.join("\n");

  return lines
    .map((line) => (line.startsWith(indent) ? line.slice(indent.length) : line))
    .join("\n");
}
